/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { focusCaptureId } from 'src/core/components/Page';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';

export type SkipToMainContentLinkProps = {
    /**
     * Determines which styling theme is used.
     */
    isDarkTheme?: boolean;
};

const messages = defineMessages({
    skipToMainContent: {
        id: 'portal.skipToMainContentLink.skipToMainContent',
        defaultMessage: 'Skip to main content'
    }
});

const { styleComponent } = getStylistV2('SkipToMainContentLink');

const SkipLink = styleComponent<ILinkProps>(Link)(
    'SkipLink',
    (theme, cssVars) => ({
        width: '0px',
        height: '0px',
        overflow: 'hidden',
        $nest: {
            '&:focus': {
                left: 0,
                right: 0,
                zIndex: theme.zIndex as Property.ZIndex,
                textDecoration: 'underline',
                textAlign: 'center',
                width: 'auto',
                height: 'auto',
                outline: 'none',
                padding: theme.padding,
                position: 'fixed',
                color: cssVars.color,
                backgroundColor: cssVars.backgroundColor
            }
        }
    }),
    {
        cssVars: { color: '', backgroundColor: '' },
        cssVarsSetter: ({ isDarkTheme }: { isDarkTheme: boolean }, theme) => ({
            color: isDarkTheme ? theme.darkThemeTextColor : theme.lightThemeTextColor,
            backgroundColor: isDarkTheme ? theme.darkThemeBackgroundColor : theme.lightThemeBackgroundColor
        })
    }
);

/**
 * This link is mandatory for keyboard accessibility,
 * for more info: https://www.w3.org/TR/WCAG20-TECHS/G1.html.
 * It's hidden by default and shows when the user navigate the header using tab key.
 */
export const SkipToMainContentLink = (props: SkipToMainContentLinkProps) => {
    const { isDarkTheme } = props;
    const { localize } = useLocalization();

    return (
        <SkipLink id="skipToMainContentLink" customProps={{ isDarkTheme }} href={`#${focusCaptureId}`}>
            {localize(messages.skipToMainContent)}
        </SkipLink>
    );
};
